/**
 * SOLID Review
 * S — Strong. One concern: synthesize the welcome screen's dice-rolling sound — the low rumble of a shaken cup and the dice's impact
 *     hits — on a context of its own, which it hands back to be closed.
 * O — Good. The sound's profile (`IConfigRumble`, `IEntityHitSequence`) is data, exported as `DEFAULT_RUMBLE_CONFIG` /
 *     `DEFAULT_HIT_SEQUENCE` and accepted as arguments: a new variant is a new profile handed in, not an edit here.
 * L — N/A — no hierarchy.
 * I — Strong. One method.
 * D — Strong. The platform's audio context, randomness and timers.
 */
import type { IServiceViewTimers } from './platform/IConfigDepViewPlatform';

export interface IConfigRumble {
  readonly startFreq: number;
  readonly endFreq: number;
  readonly lpfFreq: number;
  readonly lpfQ: number;
  readonly peakGain: number;
  readonly durationSec: number;
}

export interface IEntityHitSequence {
  readonly hits: ReadonlyArray<{ t: number; v: number; f: number }>;
}

export const DEFAULT_RUMBLE_CONFIG: IConfigRumble = {
  startFreq: 55,
  endFreq: 35,
  lpfFreq: 100,
  lpfQ: 2,
  peakGain: 0.07,
  durationSec: 3,
};

export const DEFAULT_HIT_SEQUENCE: IEntityHitSequence = {
  hits: [
    { t: 0.06, v: 0.38, f: 920  }, { t: 0.18, v: 0.30, f: 760  },
    { t: 0.31, v: 0.42, f: 1080 }, { t: 0.45, v: 0.35, f: 870  },
    { t: 0.60, v: 0.44, f: 1020 }, { t: 0.74, v: 0.32, f: 720  },
    { t: 0.89, v: 0.40, f: 1150 }, { t: 1.04, v: 0.36, f: 960  },
    { t: 1.20, v: 0.38, f: 820  }, { t: 1.36, v: 0.42, f: 1070 },
    { t: 1.52, v: 0.28, f: 890  }, { t: 1.70, v: 0.32, f: 760  },
    { t: 1.88, v: 0.28, f: 1100 }, { t: 2.06, v: 0.22, f: 850  },
    { t: 2.24, v: 0.20, f: 950  }, { t: 2.42, v: 0.16, f: 800  },
    { t: 2.60, v: 0.13, f: 700  }, { t: 2.78, v: 0.09, f: 880  },
  ],
};

/**
 * THE WELCOME CUPS' SOUND (frontend-framework-oo P3, 2026-10-29, §28.295) — `components/auth/welcomeScreen/playDiceRollingSound.ts`
 * until P3, which read `window`'s Web Audio and `Math.random` itself; an object the factory builds over the platform's now. Each play
 * makes its own context (the screen closes the last one before the next, so a replay does not stack two rattles) and closes it a
 * moment after the sound ends; a browser with no Web Audio, or one that refuses a context, plays nothing and the animation goes on.
 */
export class DiceRollingSound {
  constructor(
    private readonly createContext: () => AudioContext,
    private readonly random: () => number,
    private readonly timers: IServiceViewTimers,
  ) {}

  /**
   * `delaySec` schedules the whole sound that many seconds into the future (on the context's clock) so it lines up with the shake
   * phase of the animation; the context itself is made now. `rumble`/`sequence` default to the built-in profile.
   */
  play(
    delaySec = 0,
    rumble: IConfigRumble = DEFAULT_RUMBLE_CONFIG,
    sequence: IEntityHitSequence = DEFAULT_HIT_SEQUENCE,
  ): AudioContext | null {
    try {
      const ctx = this.createContext();
      const t0 = ctx.currentTime + delaySec;

      // Low rumble of cup being shaken
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(rumble.startFreq, t0);
      osc.frequency.exponentialRampToValueAtTime(rumble.endFreq, t0 + rumble.durationSec);

      const oscLpf = ctx.createBiquadFilter();
      oscLpf.type = 'lowpass';
      oscLpf.frequency.value = rumble.lpfFreq;
      oscLpf.Q.value = rumble.lpfQ;

      const oscGain = ctx.createGain();
      oscGain.gain.setValueAtTime(0, t0);
      oscGain.gain.linearRampToValueAtTime(rumble.peakGain, t0 + 0.15);
      oscGain.gain.setValueAtTime(rumble.peakGain, t0 + rumble.durationSec - 0.5);
      oscGain.gain.linearRampToValueAtTime(0, t0 + rumble.durationSec);

      osc.connect(oscLpf);
      oscLpf.connect(oscGain);
      oscGain.connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + rumble.durationSec + 0.1);

      // Dice impact hits (noise bursts, descending amplitude)
      for (const { t, v, f } of sequence.hits) {
        const dur = 0.03 + this.random() * 0.055;
        const bufLen = Math.floor(ctx.sampleRate * dur);
        const buf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
        const data = buf.getChannelData(0);
        for (let j = 0; j < bufLen; j++) data[j] = this.random() * 2 - 1;

        const src = ctx.createBufferSource();
        src.buffer = buf;

        const bpf = ctx.createBiquadFilter();
        bpf.type = 'bandpass';
        bpf.frequency.value = f + (this.random() - 0.5) * 200;
        bpf.Q.value = 1.2 + this.random() * 0.8;

        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0, t0 + t);
        gain.gain.linearRampToValueAtTime(v, t0 + t + 0.003);
        gain.gain.exponentialRampToValueAtTime(0.001, t0 + t + dur);

        src.connect(bpf);
        bpf.connect(gain);
        gain.connect(ctx.destination);
        src.start(t0 + t);
        src.stop(t0 + t + dur + 0.02);
      }

      /* c8 ignore next -- the delayed close fires after a test's end; its callback runs in no unit test */
      this.timers.setTimeout(() => { try { ctx.close(); } catch { /* intentionally suppress */ } }, (delaySec + rumble.durationSec + 0.6) * 1000);
      return ctx;
    } catch {
      // AudioContext unavailable or blocked — animation still plays
      return null;
    }
  }
}
